import React from 'react';
import * as StringHelpers from '../../../helpers/string';
import './audio.css';

const constraints = {
	video: false,
	audio: { echoCancellation: { exact: true } },
};

const mediaSource = 'MediaSource' in window ? new MediaSource() : null;

let recordedBlobs = [[]];
let streamingURLs = [];

const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, { type: contentType });
	return [blob];
};

export class AudioRecorder extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			error: '',
			started: false,
			recording: false,
			previewing: false,
			playing: false,
			hasBlob: false,
			clock: null,
			readying: null,
		};

		if (props.mediaFile) {
			const mediaFile = JSON.parse(props.mediaFile);
			recordedBlobs[props.position] = b64toBlob(mediaFile.src.replace('data:audio/webm;base64,', ''), 'audio/webm');
		}

		if (props.streamingUrl) {
			streamingURLs[props.position] = `${this.props.streamingPrefix}/${this.props.streamingUrl}`;
		}

		this.onStart = this.onStart.bind(this);
		this.onPlay = this.onPlay.bind(this);
		this.onRecord = this.onRecord.bind(this);
		this.onReady = this.onReady.bind(this);
		this.stopAudio = this.stopAudio.bind(this);
		this.handleDataAvailable = this.handleDataAvailable.bind(this);
	}

	async onStart() {
		try {
			const stream = await navigator.mediaDevices.getUserMedia(constraints);
			this.handleSuccess(stream);
		} catch (e) {
			console.error('navigator.getUserMedia error:', e);
		}
	}

	handleSuccess(stream) {
		this.setState({ started: true });
		window.stream = stream;
	}

	onReady() {
		const ready = this.state.readying;
		const countDownFrom = 3;
		if (ready === 0) {
			this.setState({ readying: null });
			this.onRecord();
		} else {
			setTimeout(() => {
				this.setState({ readying: ready === null ? countDownFrom : ready - 1 });
				this.onReady();
			}, 1000);
		}
	}

	onRecord() {
		const { recording } = this.state;
		if (!recording) {
			this.setState({ clock: 0 });
			this.runRecordClock = setInterval(() => this.setState({ clock: this.state.clock + 1 }), 1000);
			this.startRecording();
		} else {
			this.stopRecording();
			clearInterval(this.runRecordClock);
		}

		this.setState({ recording: !recording, previewing: false });
	}

	stopAudio() {
		clearInterval(this.runPlayClock);
		this.setState({ clock: 0, playing: false });
		this.audio && this.audio.pause();
		if (this.audio) this.audiocurrentTime = 0;
	}

	onPlay() {
		const { position, onPlay } = this.props;
		onPlay && onPlay();
		const audioBlob = new Blob(recordedBlobs[position]);
		let audioUrl = URL.createObjectURL(audioBlob);

		if (!recordedBlobs[position] || recordedBlobs[position].length <= 1) {
			audioUrl = streamingURLs[position];
		}

		this.stopAudio();
		this.audio = new Audio(audioUrl);
		this.audio.play();
		this.setState({ playing: true });
		this.runPlayClock = setInterval(() => this.setState({ clock: this.state.clock + 1 }), 1000);
		this.audio.addEventListener('ended', () => clearInterval(this.runPlayClock));
	}

	startRecording() {
		const { position } = this.props;
		recordedBlobs[position] = [];

		try {
			this.mediaRecorder = new MediaRecorder(window.stream, { mimeType: 'audio/webm' });
		} catch (e) {
			console.error('Exception while creating MediaRecorder:', e);
			return;
		}

		this.mediaRecorder.onstop = event => {};

		this.mediaRecorder.ondataavailable = this.handleDataAvailable;
		this.mediaRecorder.start(10); // collect 10ms of data
		this.setState({ recording: true });
	}

	stopRecording() {
		const { position } = this.props;
		const { clock } = this.state;
		this.mediaRecorder && this.mediaRecorder.stop();
		this.setState({ recording: false, hasBlob: true });
		const blob = new Blob(recordedBlobs[position]);
		this.props.onChange && this.props.onChange(blob, clock);
	}

	handleDataAvailable(event) {
		const { position } = this.props;

		if (event.data && event.data.size > 0) {
			recordedBlobs[position].push(event.data);
		}
	}

	componentWillReceiveProps(newProps) {
		const { stopExept, position } = this.props;
		if (newProps.stopExept && newProps.stopExept !== stopExept && position !== newProps.stopExept) {
			this.stopAudio();
		}
	}

	componentDidMount() {
		if (!mediaSource) return;
		this.onStart();
	}

	render() {
		const { position } = this.props;
		const { hasBlob, recording, clock, readying, playing } = this.state;

		let duration = '--:--';
		if (typeof recordedBlobs[position] === 'object') {
			duration = '00:00';
		}

		if (!mediaSource) return <div>Sorry but this functionality is not currently supported on your device.</div>;

		return (
			<span className="audio-recorder">
				{!recording ? (
					<button onClick={this.onReady} className="btn btn-xs btn-success" title="Record dialog">
						<i className="fa fa-microphone" />
					</button>
				) : (
					<button onClick={this.onRecord} className="btn btn-xs btn-danger" title="Stop">
						<i className="fa fa-stop" />
					</button>
				)}
				{readying ? (
					<span className="audio-time mr5">-{StringHelpers.displaySeconds(readying)}</span>
				) : (
					<span className="audio-time mr5">{clock === null ? duration : StringHelpers.displaySeconds(clock)}</span>
				)}
				{playing ? (
					<button onClick={this.stopAudio} className="btn btn-xs btn-info" title="Stop">
						<i className="fa fa-circle-stop" />
					</button>
				) : (
					<button
						onClick={this.onPlay}
						disabled={(!this.props.streamingUrl && !hasBlob) || recording}
						className="btn btn-xs btn-info"
						title="Play"
					>
						<i className="fa fa-circle-play" />
					</button>
				)}
			</span>
		);
	}
}

export default AudioRecorder;
